/**
 * Hand-off for the just-uploaded image's local blob, keyed by attachment id.
 *
 * The gap this closes: on a successful upload the orchestrator swaps the
 * node's attrs from `uploadingBlobUrl` to `attachmentId`. The NodeView
 * then has an id but no URL, so it renders a spinner box while it (1)
 * fetches a signed S3 URL and (2) waits for the browser to fetch the
 * bytes — two round-trips, ~1-1.5s measured, during which the image the
 * user just watched upload is replaced by an empty box that also
 * collapses the layout. The bytes were in memory the whole time.
 *
 * So the blob is retained here instead of being revoked at swap time,
 * and the NodeView keeps painting it until the real image has decoded.
 *
 * **Why a module registry rather than keeping `uploadingBlobUrl` on the
 * node:** `stripInFlightPlaceholderNodes` drops whole NODES, it does not
 * strip attrs — an image carrying both `attachmentId` and
 * `uploadingBlobUrl` passes the strip untouched, so the `blob:` URL
 * would persist into the draft, version rows and IndexedDB, and be dead
 * on the next load. Keeping the bridge outside the document means no
 * transient value can ever reach a serializer.
 *
 * Entries self-expire: a node that never mounts (the user navigated away
 * mid-upload) would otherwise pin its bytes for the life of the tab.
 */

/** Bytes are held at most this long before being reclaimed. */
const RETENTION_MS = 60_000

interface Entry {
  blobUrl: string
  timer: ReturnType<typeof setTimeout>
}

const entries = new Map<string, Entry>()

/**
 * Retain `blobUrl` for `attachmentId` instead of revoking it. The caller
 * transfers ownership — it must NOT revoke the URL afterwards.
 */
export function retainUploadBlob(attachmentId: string, blobUrl: string): void {
  releaseUploadBlob(attachmentId)
  const timer = setTimeout(() => releaseUploadBlob(attachmentId), RETENTION_MS)
  entries.set(attachmentId, { blobUrl, timer })
}

/** The retained blob URL for this attachment, or null. Does not consume. */
export function peekUploadBlob(attachmentId: string | null): string | null {
  if (!attachmentId) return null
  return entries.get(attachmentId)?.blobUrl ?? null
}

/**
 * Revoke and forget the retained blob. Idempotent — the expiry timer and
 * the NodeView's load handler both call it, and whichever runs second is
 * a no-op.
 */
export function releaseUploadBlob(attachmentId: string | null): void {
  if (!attachmentId) return
  const entry = entries.get(attachmentId)
  if (!entry) return
  clearTimeout(entry.timer)
  entries.delete(attachmentId)
  URL.revokeObjectURL(entry.blobUrl)
}

/** Test-only reset so a suite can't leak state between cases. */
export function __resetUploadBlobBridgeForTests(): void {
  for (const id of [...entries.keys()]) releaseUploadBlob(id)
}
